/**
 * Check 6 — is the link in this caption actually clickable, and therefore
 * actually attributable?
 *
 * THIS CHECK MAKES AN ORPHAN COLUMN LIVE (E7). `PlatformSpec.caption.
 * linkPolicy` has existed for a long time with exactly ONE non-test reader:
 * `src/lib/ai-agent/system-prompt/platform-specs-block.ts:13`, which
 * interpolates it into a system prompt as ADVICE to the model. Nothing
 * enforced it anywhere. This is its first enforcing consumer.
 *
 * URL detection goes through `extractFirstCaptionUrl`, which must stay
 * identical to the UTM stamper's own extractor — the check predicts what the
 * stamper will do, so the two disagreeing would be a silent no-op.
 */
import { platformDisplayName } from '../../../../shared/posting-readiness.ts';
import { getPlatformSpec } from '../../../constants/platform-specs.ts';
import { extractFirstCaptionUrl } from '../extract-url.ts';
import { CHECK_LABEL, type QualityCheck } from '../types.ts';

/**
 * Points at a LONGER PIECE, not merely at a link. "Link in bio" is excluded on
 * purpose — on a bio-only platform it is a true statement about where the link
 * lives, and flagging it would be the over-firing that trains users to ignore
 * the chip.
 */
const PROMISES_A_LONGER_PIECE =
  /\b(?:read|see|check out|catch|find)\s+(?:the\s+)?(?:full|whole|complete|entire|rest of the)\s+(?:breakdown|guide|story|thread|article|post|write-?up|case study|piece|analysis|report|teardown)\b|\bfull\s+(?:breakdown|write-?up|teardown|case study)\s+(?:below|here|in the (?:comments|link|thread))\b|\b(?:link|details|more)\s+in\s+the\s+comments\b/i;

export function checkLink(input: {
  platform: string;
  caption: string;
  hasCommerceContext: boolean;
}): QualityCheck {
  const { platform, caption, hasCommerceContext } = input;
  const base = { id: 'link' as const, label: CHECK_LABEL.link };
  const name = platformDisplayName(platform);
  const spec = getPlatformSpec(platform.toLowerCase().trim());
  const url = extractFirstCaptionUrl(caption);

  if (!spec) {
    return { ...base, status: 'skipped', detail: `Mora has no link rules on record for ${name}.` };
  }

  const policy = spec.caption.linkPolicy;

  if (url) {
    if (policy === 'bio-only') {
      // WARN, never FAIL. Blocking a post for containing a URL would be
      // strictly worse than today's behaviour, and the link is not an error —
      // it just cannot earn attribution here.
      //
      // NO FIX IN THIS LANE: the bio-link hop and per-post discount codes are
      // Lane G1's deliverable. When G1 lands this becomes fix id
      // 'swap_to_bio_link' and nothing else in this file changes.
      return {
        ...base,
        status: 'warn',
        detail: `${name} captions are not clickable — this link will show as plain text and no revenue can be attributed to it. Use a discount code or a bio link instead.`,
      };
    }
    if (policy === 'first-comment') {
      return {
        ...base,
        status: 'warn',
        detail: `${name} links belong in the first comment, not the caption.`,
      };
    }
    return {
      ...base,
      status: 'pass',
      detail: `This link is clickable on ${name} and will be tracked.`,
    };
  }

  // The (c)/(d) split below is load-bearing: warning on EVERY link-free post
  // would make the chip noise, which is how a check trains users to ignore it.
  if (hasCommerceContext) {
    return {
      ...base,
      status: 'warn',
      detail: 'No link in this caption, so this post cannot be tied back to revenue.',
    };
  }

  // 🔴 A PROMISE OF SOMETHING THAT DOES NOT EXIST (2026-08-01).
  //
  // Tracked in the run log since run 4 as "fabricated read the full…" (4/40,
  // then 2/40) and reproduced live on 2026-08-01: a caption closed with
  // "Read the full breakdown — link in the comments" when there was no
  // breakdown, no link and no source article. A reader who goes looking finds
  // nothing, which is the same failure as an invented product — the caption
  // asserts an artifact into existence.
  //
  // Fires only when ALL of these hold, because the false-positive cost is high:
  //   - the caption points at a LONGER PIECE (breakdown, guide, thread, article,
  //     write-up, case study) — not merely at a link;
  //   - there is no URL in the caption (checked above);
  //   - `hasCommerceContext` is EXPLICITLY false, i.e. the post carries no
  //     shopify_product_id, source_url or source_article_id, so no such piece
  //     exists anywhere;
  //   - undefined stays `skipped`, never a pass. Same discipline as
  //     `sourceRatio` and `salesGoal`: a guess reported as a result is the bug.
  //
  // "Link in bio" is deliberately NOT matched — on a bio-only platform that is
  // a true statement about where the link lives.
  if (hasCommerceContext === false && PROMISES_A_LONGER_PIECE.test(caption)) {
    return {
      ...base,
      status: 'fail',
      detail:
        'This caption points readers to a longer piece — a breakdown, guide or thread — that does not exist. ' +
        'Nothing was linked and no source article is attached, so anyone who goes looking finds nothing. ' +
        'Either attach the piece or cut the promise.',
    };
  }

  return { ...base, status: 'skipped', detail: 'No link in this caption.' };
}
